import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mountain, AlertTriangle, ArrowLeft, Trash2, CheckCircle } from 'lucide-react';
import { VerifyIdentityPage } from './VerifyIdentityPage';

interface DeleteAccountPageProps {
  onBack: () => void;
  onDeleted: () => void;
}

export const DeleteAccountPage: React.FC<DeleteAccountPageProps> = ({ onBack, onDeleted }) => {
  const { deleteAccount, isRecentlyAuthenticated } = useAuth();
  const [purchaseAcknowledged, setPurchaseAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(isRecentlyAuthenticated);

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!purchaseAcknowledged) return;

    setLoading(true);
    setError('');

    try {
      await deleteAccount();
      setLoading(false);
      setDeleted(true);
      // Redirect to sign-in after a brief delay
      setTimeout(() => onDeleted(), 2500);
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setVerified(false);
      } else {
        setError(err.message || 'Failed to delete account');
      }
      setLoading(false);
    }
  };

  if (!verified) {
    return <VerifyIdentityPage onVerified={() => setVerified(true)} onBack={onBack} />;
  }

  if (deleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Account Deleted
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account and all associated data have been permanently deleted.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <p className="text-sm text-gray-500">Redirecting to sign in...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-red-500 to-red-700 p-3 rounded-xl">
              <Mountain className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Delete Account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            This action is permanent and cannot be undone.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-5">
          {/* Warning banner */}
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800 space-y-2">
                <p className="font-semibold">This will permanently delete your account.</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All of your data across <strong>all Roam Apps</strong> will be permanently deleted.</li>
                  <li>This includes Roam Weather and any other connected services.</li>
                  <li>Any purchases you have made will be <strong>permanently lost</strong> and cannot be restored or refunded.</li>
                  <li>You will not be able to recover your account or any associated data.</li>
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleDelete} className="space-y-4">
            {/* Acknowledgment checkbox */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={purchaseAcknowledged}
                onChange={(e) => setPurchaseAcknowledged(e.target.checked)}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                I understand that any purchases I have made will be <strong>permanently lost</strong> and cannot be restored or refunded.
              </span>
            </label>

            {/* Delete button */}
            <button
              type="submit"
              disabled={!purchaseAcknowledged || loading}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              {loading ? 'Deleting account...' : 'Permanently delete my account'}
            </button>
          </form>

          <div className="pt-2 border-t">
            <button
              onClick={onBack}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
